import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  Button,
  Grid,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box, Container, Stack } from '@mui/system';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import Countdown from 'react-countdown';

import BusinessTaxSubMenu from '@/components/nav/BusinessTaxSubMenu';
import ExitPopupBusinessTax from '@/components/ui/ExitPopupBusinessTax';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
import { getLastDateOfTheMonth } from '@/utils/utils';

const BusinessTax = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [closeSticky, setCloseSticky] = useState<boolean>(false);
  // const [progress, setProgress] = useState(0);
  const handleStickyClose = (): void => {
    setCloseSticky(true);
  };
  const images = [
    <img
      src="/assets/images/small-business-own-img.webp"
      key="first"
      alt="Small Business Owners"
      id="loading1"
      className="details-images"
    />,
    <img
      src="/assets/images/partnerships-img.webp"
      key="second"
      alt="Partnerships & LLCs"
      className="details-images"
    />,
    <img
      src="/assets/images/s-corporation-img.webp"
      key="third"
      alt="S-Corporations"
      className="details-images"
    />,
    <img
      src="/assets/images/cpa-tax-firms-img.webp"
      key="fourth"
      alt="CPAs & Tax Firms"
      className="details-images"
    />,
  ];
  const loader = ['loading1', 'loading2', 'loading3', 'loading4'];
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (currentIndex === images.length - 1) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(currentIndex + 1);
      }
    }, 10000);

    return () => clearInterval(intervalId);
  }, [currentIndex + 1]);
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (currentIndex === loader.length - 1) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(currentIndex + 1);
      }
    }, 10000);

    return () => clearInterval(intervalId);
  }, [currentIndex + 1]);

  const [lastDayTaxSeason, setLastDayTaxSeason] = useState(new Date());

  useEffect(() => {
    setLastDayTaxSeason(getLastDateOfTheMonth(new Date().getFullYear(), 3, 16));
    setInterval(() => {
      setLastDayTaxSeason(
        getLastDateOfTheMonth(new Date().getFullYear(), 3, 16)
      );
    }, 1000); // Updates every second (adjust if needed)
  }, []);

  // Random component
  const Completionist = () => <span />;

  // Renderer callback with condition
  const renderer = ({
    days,
    hours,
    minutes,
    seconds,
    completed,
  }: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    completed: boolean;
  }) => {
    if (completed) {
      // Render a completed state
      return <Completionist />;
    }
    // Render a countdown
    return (
      <Stack
        direction="row"
        justifyContent={{ xs: 'center', md: 'flex-start' }}
        alignItems="center"
        spacing={1}
        sx={{
          '& .timer-count': {
            border: '1px solid #848484',
            p: 1.5,
            textAlign: 'center',
            minWidth: { xs: 55, sm: 70, md: 70 },
            position: 'relative',
            borderRadius: 2,
            boxShadow: ' 0 0 20px rgba(0,0,0,.12)',
          },
          '& .timer-number': {
            fontSize: { xs: 18, sm: 24 },
            fontWeight: 900,
          },
          '& .colon': {
            fontSize: { xs: 18, sm: 24 },
            px: { xs: 0.2, sm: 0.5 },
            py: 0,
            mt: { xs: '-8px !important', sm: '-14px !important' },
          },
          '& .timer-label': {
            background: '#000',
            borderTop: '1px solid #848484',
            fontSize: { xs: 10, sm: 12 },
            fontWeight: 500,
            mt: 0,
            mx: -1.5,
            mb: -1.5,
            py: 0.2,
            px: { xs: 0.5, sm: 1, lg: 0.5 },
            borderRadius: '0px 0px 8px 8px',
          },
        }}
      >
        <Box className="timer-count">
          <Box className="timer-number">
            {days?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Days</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {hours?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Hours</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {minutes?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Minutes</Box>
        </Box>
        <Box className="colon">:</Box>
        <Box className="timer-count">
          <Box className="timer-number">
            {seconds?.toString().padStart(2, '0')}
          </Box>
          <Box className="timer-label">Seconds</Box>
        </Box>
      </Stack>
    );
  };
  return (
    <AppLayout
      meta={
        <Meta
          title="TaxZerone Business Tax Hub | File All IRS Business Forms Online"
          description="E-file your business taxes in one place. Partnerships, LLCs, and S-corps can prepare, validate, and submit IRS forms securely with TaxZerone."
          keywords=""
          canonical="business-tax/"
          jsonLdPricing=""
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="1658"
          ogShareImg=""
        />
      }
    >
      <Hidden smDown>
        {!closeSticky && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              width: '100%',
              left: 0,
              right: 0,
              zIndex: 3,
            }}
          >
            <Container
              maxWidth="md"
              sx={{
                backgroundColor: '#fff',
                mx: { xs: 3, lg: 'auto' },
                width: 'auto',
                py: 1.5,
                borderTopLeftRadius: 6,
                borderTopRightRadius: 6,
                boxShadow: '0 0 20px rgba(0,0,0,.5)',
                position: 'relative',
                borderWidth: 2,
                borderColor: '#414042',
                borderStyle: 'solid',
                borderBottom: 'none',
              }}
            >
              <Tooltip title="Close" arrow placement="top">
                <IconButton
                  aria-label="close"
                  size="small"
                  onClick={handleStickyClose}
                  sx={{
                    position: 'absolute',
                    right: -12,
                    top: -12,
                    color: '#fff',
                    backgroundColor: '#e11d1d',
                    '&:hover, &:focus': {
                      backgroundColor: '#b01919',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: 1, sm: 1, md: 2 }}
                sx={{ justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Box>
                  <Typography
                    component="div"
                    variant="body1"
                    sx={{
                      textAlign: { xs: 'center', sm: 'left' },
                      fontSize: { xs: 15, sm: 17, md: 18, lg: '1.25rem' },
                      //  color: '#ccc',
                      lineHeight: 1.3,
                    }}
                  >
                    <strong>Thank you</strong> for trusting{' '}
                    <b style={{ fontWeight: 600, color: '#213e99' }}>
                      TaxZerone
                    </b>{' '}
                    with your <strong>business tax</strong> filing.
                  </Typography>
                </Box>
                {/* <Stack direction="row" alignItems="center" spacing={2}>
                  <Countdown date={lastDayTaxSeason} renderer={renderer} />
                  <Hidden mdUp>
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        size="medium"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=businesstax`}
                        sx={{
                          textTransform: 'none',
                          px: 2.5,
                          display: 'inline-block',
                          boxShadow: 'none',
                          borderRadius: 4.2,
                          backgroundColor: '#213e99',
                          whiteSpace: 'nowrap',
                          '&:hover, &:focus': {
                            backgroundColor: '#172b6b',
                          },
                        }}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Hidden>
                </Stack> */}
                {/* <Hidden mdDown>
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      size="medium"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=businesstax`}
                      sx={{
                        textTransform: 'none',
                        px: 2.5,
                        display: 'inline-block',
                        boxShadow: 'none',
                        borderRadius: 4.2,
                        backgroundColor: '#213e99',
                        whiteSpace: 'nowrap',
                        '&:hover, &:focus': {
                          backgroundColor: '#172b6b',
                        },
                      }}
                    >
                      E-File Now
                    </Button>
                  </Box>
                </Hidden> */}
              </Stack>
            </Container>
          </Box>
        )}
      </Hidden>
      <ExitPopupBusinessTax />
      <BusinessTaxSubMenu />

      <Container
        component="section"
        sx={{
          position: 'relative',
          mt: 2,
          mb: 0,
        }}
        maxWidth="lg"
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #5a189a, #240046)',
            p: 1.5,
            px: 4,
            color: '#fff',
          }}
          className="ribbon"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Typography
              variant="body1"
              sx={{
                textAlign: { xs: 'center', sm: 'left' },
                fontSize: { xs: 16, sm: 17, md: 20, lg: '1.25rem' },
                color: '#e0e0e0',
              }}
            >
              <strong style={{ fontWeight: 600 }}>
                Filed a Business Extension?{' '}
              </strong>{' '}
              E-file Your{' '}
              <span style={{ fontWeight: 600, color: '#eee' }}>
                {' '}
                2025 Form 1120-S or Form 1065
              </span>{' '}
              Today & Distribute Schedule K-1s on Time
            </Typography>
            <Box sx={{}}>
              {/* <Typography
                variant="body2"
                sx={{
                  textAlign: 'center',
                  mb: { xs: 1, sm: 1 },
                  lineHeight: 1.3,
                }}
              >
                Time left to file before the deadline{' '}
                <Hidden smUp>
                  <br />
                </Hidden>
                (
                <span style={{ fontWeight: 600, color: '#fff' }}>
                  March 16, 2026
                </span>
                ):
              </Typography> */}
              <Countdown date={lastDayTaxSeason} renderer={renderer} />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="small"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=businesstax`}
                sx={{
                  backgroundColor: '#ee4266',
                  '&:hover, &:focus': {
                    backgroundColor: '#213e99',
                    color: '#fff',
                  },
                  px: 2,
                  color: '#fff',
                  mt: { xs: 0, md: 0 },
                  display: { xs: 'inline-block', sm: 'inline-block' },
                  mx: { xs: 'auto', sm: 1 },
                  borderRadius: 3,
                  boxShadow: 'none',
                  fontSize: 18,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                E-file Now!
              </Button>
              {/* <Typography
                variant="body2"
                sx={{ textAlign: 'center', mt: 1, color: '#fff' }}
              >
                {' '}
                <b style={{ color: '#fff' }}> Save 10%</b> with code{' '}
                <b style={{ color: '#f4d58d' }}> TZOTE10</b>
              </Typography> */}
            </Box>
          </Stack>
        </Box>
      </Container>
      <Box sx={{ maxWidth: 1920, mx: 'auto' }} component="section">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="header"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                File Every{' '}
                <span className="highlight-container">
                  <span className="highlight"> Business Tax Return</span>
                </span>{' '}
                from One Secure Platform
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                Partnerships, S-corps, and complex business entities trust
                TaxZerone to file IRS forms accurately, on time, and without tax
                software headaches.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, sm: 2, md: 6 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4, mb: { xs: 4, sm: 0 } }}
              >
                <Button
                  variant="contained"
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=businesstax`}
                  sx={{
                    mr: { xs: 0, md: 4 },
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                >
                  Start Filing
                </Button>
                <Button
                  variant="contained"
                  className="btn-secondary-outline"
                  href="#supported-forms"
                  sx={{
                    mr: { xs: 0, md: 0 },
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                >
                  See Supported Forms
                </Button>
              </Stack>
              <Stack
                direction="row"
                spacing={2}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Box
                  sx={{
                    mb: { xs: '0 !important', md: '0px !important' },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/irs-authorized.webp" />
                    <source srcSet="/assets/images/irs-authorized.png" />
                    <Box
                      component="img"
                      src="/assets/images/irs-authorized.png"
                      loading="lazy"
                      alt="IRS-authorized e-file provider"
                      width={70}
                      height={69}
                      sx={{
                        width: { xs: 50, md: 70 },
                        height: { xs: 49, md: 69 },
                      }}
                    />
                  </picture>
                </Box>
                <Box
                  sx={{
                    mb: { xs: '0px !important', md: '0px !important' },
                  }}
                >
                  <picture>
                    <source srcSet="/assets/images/tzo-soc2.webp" />
                    <source srcSet="/assets/images/tzo-soc2.png" />
                    <Box
                      component="img"
                      src="/assets/images/tzo-soc2.png"
                      loading="lazy"
                      alt="TZO SOC2 Compliant"
                      width={90}
                      height={89}
                      sx={{
                        width: { xs: 70, md: 90 },
                        height: { xs: 69, md: 89 },
                      }}
                    />
                  </picture>
                </Box>
              </Stack>
              {/* <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={0}
              m={0}
              mt={{ xs: 2, md: 5 }}
              pb={0}
              textAlign="center"
              sx={{ ml: { xs: 0, md: -5 } }}
            >
              <Grid size={{ xs: 4, md: 4, sm: 4 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    borderWidth: 0,
                    fontSize: { xs: 9, sm: 12, lg: 13 },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#efefef',
                      borderRadius: '50%',
                      width: { xs: 40, sm: 70 },
                      height: { xs: 40, sm: 70 },
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mx: 'auto',
                      mb: { xs: 0.5, sm: 1 },
                      p: { xs: 1, sm: 2 },
                      textAlign: 'center',
                    }}
                  >
                    <picture style={{ width: '100%', height: '100%' }}>
                      <source
                        srcSet="/assets/images/secure-new-icon.webp"
                        width={'100%'}
                        height={'100%'}
                      />
                      <source
                        srcSet="/assets/images/secure-new-icon.png"
                        width={'100%'}
                        height={'100%'}
                      />
                      <img
                        src="/assets/images/secure-new-icon.png"
                        alt="Safe & Secure"
                        loading="lazy"
                        width={'100%'}
                        height={'100%'}
                      />
                    </picture>
                  </Box>
                  <strong>
                    Safe &
                    <Hidden lgUp mdDown>
                      <br />
                    </Hidden>{' '}
                    Secure
                  </strong>
                </Paper>
              </Grid>
              <Grid size={{ xs: 4, md: 4, sm: 4 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    borderWidth: 0,
                    fontSize: { xs: 9, sm: 12, lg: 13 },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#efefef',
                      borderRadius: '50%',
                      width: { xs: 40, sm: 70 },
                      height: { xs: 40, sm: 70 },
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mx: 'auto',
                      mb: { xs: 0.5, sm: 1 },
                      p: { xs: 1, sm: 2 },
                      textAlign: 'center',
                    }}
                  >
                    <picture style={{ width: '100%', height: '100%' }}>
                      <source
                        srcSet="/assets/images/irs-new-icon.webp"
                        width={'100%'}
                        height={'100%'}
                      />
                      <source
                        srcSet="/assets/images/irs-new-icon.png"
                        width={'100%'}
                        height={'100%'}
                      />
                      <img
                        src="/assets/images/irs-new-icon.png"
                        alt="IRS Authorized"
                        loading="lazy"
                        width={'100%'}
                        height={'100%'}
                      />
                    </picture>
                  </Box>
                  <strong>
                    IRS
                    <Hidden lgUp mdDown>
                      <br />
                    </Hidden>{' '}
                    Authorized
                  </strong>
                </Paper>
              </Grid>
              <Grid size={{ xs: 4, md: 4, sm: 4 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 1, sm: 2 },
                    borderRadius: 0,
                    borderWidth: 0,
                    fontSize: { xs: 9, sm: 12, lg: 13 },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#efefef',
                      borderRadius: '50%',
                      width: { xs: 40, sm: 70 },
                      height: { xs: 40, sm: 70 },
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mx: 'auto',
                      mb: { xs: 0.5, sm: 1 },
                      p: { xs: 1, sm: 2 },
                      textAlign: 'center',
                    }}
                  >
                    <picture style={{ width: '100%', height: '100%' }}>
                      <source
                        srcSet="/assets/images/lowest-new-icon.webp"
                        width={'100%'}
                        height={'100%'}
                      />
                      <source
                        srcSet="/assets/images/lowest-new-icon.png"
                        width={'100%'}
                        height={'100%'}
                      />
                      <img
                        src="/assets/images/lowest-new-icon.png"
                        alt="Affordable Price"
                        loading="lazy"
                        width={'100%'}
                        height={'100%'}
                      />
                    </picture>
                  </Box>
                  <strong>
                    Affordable
                    <Hidden lgUp mdDown>
                      <br />
                    </Hidden>{' '}
                    Filing Fee
                  </strong>
                </Paper>
              </Grid>
            </Grid> */}
            </Box>
          </Box>
          <Hidden lgDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
              }}
              className="business-tax"
            >
              <picture
                style={{ marginBottom: -7, left: -30, position: 'relative' }}
              >
                <source
                  srcSet="/assets/images/business-tax-img.webp"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <source
                  srcSet="/assets/images/business-tax-img.png"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <img
                  src="/assets/images/business-tax-img.png"
                  alt="File Every Business Tax Return from One Secure Platform"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
              </picture>
            </Box>
          </Hidden>
          <Hidden lgUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
              className="business-tax"
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/business-tax-img-sm.webp"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <source
                  srcSet="/assets/images/business-tax-img-sm.png"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <img
                  src="/assets/images/business-tax-img-sm.png"
                  alt="File Every Business Tax Return from One Secure Platform"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden>
        </Stack>
      </Box>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        component="section"
        aria-labelledby="one-form"
      >
        <Container maxWidth="lg" sx={{ mb: 5, mt: 4 }}>
          <Grid
            container
            spacing={2}
            direction={{ xs: 'column-reverse', sm: 'row' }}
          >
            <Grid size={{ xs: 12, md: 6, sm: 6 }} sx={{ mt: 5, mb: 3 }}>
              <Typography
                variant="h2"
                id="one-form"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 1.5,
                  textAlign: 'left',
                }}
              >
                Your Business Doesn’t File One Form — It Files a Tax System
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontSize: { md: 18, sm: 16, xs: 16 },
                  textAlign: 'left',
                }}
              >
                Most businesses don’t just file a return. They file:
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyleType: 'none',
                  paddingLeft: 0,
                  textAlign: 'left',
                  ml: { xs: 0, sm: 3 },
                  mt: { xs: 0, md: 3 },
                  mb: { xs: 0, md: 3 },
                  '& li': { display: 'flex' },
                }}
              >
                {' '}
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box> One entity tax return</Box>
                </li>
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box>Multiple schedules</Box>
                </li>
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box>Partner or shareholder statements</Box>
                </li>
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box>Payroll tax forms</Box>
                </li>
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box>And sometimes extension forms </Box>
                </li>
              </Box>
              <Typography variant="body1" sx={{ textAlign: 'left' }}>
                TaxZerone was built to handle{' '}
                <span style={{ fontWeight: 600 }}>
                  {' '}
                  entire business tax workflows,
                </span>{' '}
                not just single documents.
              </Typography>
              <Typography variant="body1" sx={{ textAlign: 'left', mt: 2 }}>
                Whether you’re a partnership issuing K-1s or an S-corp reporting
                shareholder income, this platform keeps everything connected,
                accurate, and IRS-compliant.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }} sx={{ mt: 4 }}>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  textAlign: { xs: 'center', md: 'right' },
                  my: { xs: 3, md: 0 },
                }}
                className="image-container"
              >
                <picture>
                  <source srcSet="/assets/images/business-tax-workflow-img.webp" />
                  <source srcSet="/assets/images/business-tax-workflow-img.png" />
                  <img
                    src="/assets/images/business-tax-workflow-img.png"
                    alt="Your Business Doesn’t File One"
                    className="img-responsive img-shadow"
                    loading="lazy"
                    width={500}
                    height={500}
                  />
                </picture>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        component="section"
        aria-labelledby="supported-forms"
      >
        <Container maxWidth="lg" sx={{ mb: 5, mt: 4 }}>
          <Grid container spacing={2}>
            <Grid size={12} sx={{ mt: 5, mb: 3 }}>
              <Typography
                variant="h2"
                id="supported-forms"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 1.5,
                  textAlign: 'center',
                  scrollMarginTop: 150,
                }}
              >
                Supported Business Tax Forms
              </Typography>
              {/* <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontSize: { md: 20, sm: 18, xs: 16 },
                  textAlign: 'center',
                }}
              >
                Business Tax Forms You Can File Right Now
              </Typography> */}

              <TableContainer
                sx={{
                  border: '1px solid #828282',
                  mb: 2,
                  maxWidth: 900,
                  mx: 'auto',
                }}
              >
                <Table sx={{}} aria-label="Supported Forms">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#ccc', fontWeight: 600 }}>
                      <TableCell
                        sx={{
                          color: '#444',
                          width: 300,
                          fontWeight: 600,
                          fontSize: 16,
                        }}
                      >
                        Business Type
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#444',
                          width: 300,
                          fontWeight: 600,
                          fontSize: 16,
                        }}
                      >
                        Form
                      </TableCell>

                      <TableCell
                        sx={{
                          color: '#444',
                          width: 300,
                          fontWeight: 600,
                          fontSize: 16,
                        }}
                      >
                        What It Covers
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ backgroundColor: '#fff' }}>
                      <TableCell>Partnerships & Multi-member LLCs </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Form 1065</TableCell>
                      <TableCell>
                        Partnership income and partner allocations{' '}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                      <TableCell>S Corporations </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Form 1120-S{' '}
                      </TableCell>
                      <TableCell>
                        Corporate income passed to shareholders{' '}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: '#fff' }}>
                      <TableCell>Extensions (All Businesses)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Form 7004 </TableCell>
                      <TableCell>Automatic filing extension </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        component="section"
      >
        <Container maxWidth="lg" sx={{ mb: 5, mt: 4 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6, sm: 12 }} sx={{ mt: 4, mb: 3 }}>
              <Box
                component="div"
                // sx={{
                //   display: 'flex',
                //   justifyContent: 'center',
                //   alignItems: 'center',
                //   height: '100%',
                //   textAlign: { xs: 'center', md: 'right' },
                //   my: { xs: 3, md: 0 },
                // }}
                className="image-container"
              >
                <Box
                  component={motion.div}
                  key={currentIndex}
                  className="image-fade"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.2, ease: 'easeInOut' }}
                  sx={{
                    '& img': {
                      width: '100%',
                      height: { xs: 'auto', md: 500 },
                      objectFit: 'cover',
                    },
                  }}
                >
                  {images[currentIndex]}
                </Box>
                <Stack
                  spacing={2}
                  direction="row"
                  sx={{
                    textAlign: 'center',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mt: 2,
                  }}
                >
                  <Box
                    component="div"
                    sx={{
                      width: 80,
                      backgroundColor: '#e791c66f',
                      height: 10,
                      borderRadius: 3,
                      my: 1,
                    }}
                    className={currentIndex === 0 ? 'loading-effect' : ''}
                    id="loading1"
                  />{' '}
                  <Box
                    component="div"
                    sx={{
                      width: 80,
                      backgroundColor: '#e791c66f',
                      height: 10,
                      borderRadius: 3,
                      my: 1,
                    }}
                    className={currentIndex === 1 ? 'loading-effect' : ''}
                    id="loading2"
                  />
                  <Box
                    component="div"
                    sx={{
                      width: 80,
                      backgroundColor: '#e791c66f',
                      height: 10,
                      borderRadius: 3,
                      my: 1,
                    }}
                    className={currentIndex === 2 ? 'loading-effect' : ''}
                    id="loading3"
                  />
                  <Box
                    component="div"
                    sx={{
                      width: 80,
                      backgroundColor: '#e791c66f',
                      height: 10,
                      borderRadius: 3,
                      my: 1,
                    }}
                    className={currentIndex === 3 ? 'loading-effect' : ''}
                    id="loading4"
                  />
                </Stack>
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 6, md: 6 }}
              sx={{
                textAlign: { md: 'left', sm: 'left' },
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
                m: 0,
              }}
            >
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 1.5,
                    textAlign: 'left',
                  }}
                >
                  Who This Platform Is Built For
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    textAlign: 'left',
                  }}
                >
                  Built for Real Business Owners — Not Just Tax Experts
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    listStyleType: 'none',
                    paddingLeft: 0,
                    textAlign: 'left',
                    ml: { xs: 0, sm: 3 },
                    mt: { xs: 0, md: 3 },
                    mb: { xs: 0, md: 3 },
                    '& li': { display: 'flex' },
                  }}
                >
                  {' '}
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Small Business Owners
                      </span>
                      <br />
                      File your business return without hiring CPAs.
                    </Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Partnerships & LLCs
                      </span>
                      <br />
                      Automatically generate K-1s and allocate income correctly.
                    </Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>
                      <span style={{ fontWeight: 600 }}> S-Corporations</span>
                      <br />
                      File your corporate return and shareholder reports in one
                      flow.
                    </Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>
                      <span style={{ fontWeight: 600 }}> CPAs & Tax Firms</span>
                      <br />
                      Handle multiple client filings from a single dashboard.
                    </Box>
                  </li>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
        component="section"
        aria-labelledby="how-business-filing-works"
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ mt: { xs: 2, sm: 6, md: 7 } }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                id="how-business-filing-works"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 0,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                How Business Filing Works on TaxZerone
              </Typography>
              {/* <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Filing your Form 1120-S is quick and easy - just follow these
                steps:
              </Typography> */}
            </Grid>
          </Grid>
        </Container>
        <Container maxWidth="xl" sx={{ mb: 5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 5 }}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                mt: { xs: -3, md: -5, lg: -8 },
              }}
            >
              <picture>
                <source
                  srcSet="/assets/images/1120s-steps-img.webp"
                  className="img-responsive"
                  width={915}
                  height={790}
                />
                <source
                  srcSet="/assets/images/1120s-steps-img.png"
                  className="img-responsive"
                  width={915}
                  height={790}
                />
                <img
                  src="/assets/images/1120s-steps-img.png"
                  alt="How Business Filing Works on TaxZerone"
                  className="img-responsive"
                  loading="lazy"
                  width={915}
                  height={790}
                />
              </picture>
            </Box>

            <Box
              sx={{
                width: '100%',
              }}
            >
              <Box
                component="ol"
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                  p: 2,
                  pl: 0,
                  ml: { xs: 0, sm: 0, md: -2 },
                }}
              >
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        my: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source
                          srcSet="/assets/images/step-1-icon.webp"
                          width={39}
                          height={39}
                        />
                        <source
                          srcSet="/assets/images/step-1-icon.png"
                          width={39}
                          height={39}
                        />
                        <img
                          src="/assets/images/step-1-icon.png"
                          alt="Enter Company Details"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Choose Your Business Type
                    </Typography>
                    <Typography variant="body1">
                      Tell us whether you’re a partnership, S-corp, or other
                      entity.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        m: 0,
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-2-icon.webp" />
                        <source srcSet="/assets/images/step-2-icon.png" />
                        <img
                          src="/assets/images/step-2-icon.png"
                          alt="Enter Business & Financial Data"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Enter Business & Financial Data
                    </Typography>
                    <Typography variant="body1">
                      Our system knows which IRS forms and schedules your
                      business needs.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-3-icon.webp" />
                        <source srcSet="/assets/images/step-3-icon.png" />
                        <img
                          src="/assets/images/step-3-icon.png"
                          alt="We Build the IRS-Ready Return"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      We Build the IRS-Ready Return
                    </Typography>
                    <Typography variant="body1">
                      TaxZerone structures everything according to IRS e-file
                      standards.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-4-icon.webp" />
                        <source srcSet="/assets/images/step-4-icon.png" />
                        <img
                          src="/assets/images/step-4-icon.png"
                          alt="Submit & Track"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Submit & Track
                    </Typography>
                    <Typography variant="body1">
                      File electronically and monitor acceptance from the IRS.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              </Box>
              {/* <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 4,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1120s`}
                >
                  Start Filing 1120-S Now
                </Button>
              </Box> */}
            </Box>
          </Stack>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
        component="section"
        aria-labelledby="top-reasons"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                id="top-reasons"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Top Reasons Businesses Choose TaxZerone
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Get more than just e-filing. TaxZerone gives you a smarter,
                simpler way to handle every business tax return.
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            m={2}
            mt={4}
            pb={{ xs: 0, sm: 4, md: 7 }}
            sx={{ justifyContent: 'center', px: 0, listStyleType: 'none' }}
            component="ul"
          >
            <Grid size={{ xs: 12, sm: 6, md: 4 }} component="li">
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/business-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/business-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/business-icon.png"
                      alt="Business-Ready Filing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Business-Ready Filing
                </Typography>
                <Typography variant="body1">
                  TaxZerone is designed for partnerships, S-corps, and growing
                  entities. Our system understands business structures,
                  schedules, and reporting requirements—so you don’t have to
                  figure it out yourself.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }} component="li">
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/guided-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/guided-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/guided-icon.png"
                      alt="Guided Business Workflows"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Guided Business Workflows
                </Typography>
                <Typography variant="body1">
                  Answer a few simple questions about your business, and we
                  automatically guide you to the correct IRS forms, schedules,
                  and supporting documents required for your entity type.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }} component="li">
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/error-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/error-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/error-icon.png"
                      alt="Smart Error Detection"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Smart Error Detection
                </Typography>
                <Typography variant="body1">
                  Our built-in validations check for missing data, calculation
                  errors, and IRS-rule mismatches before submission—helping
                  prevent delays and rejections.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12} component="li">
                &nbsp;
              </Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }} component="li">
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/irs-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/irs-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/irs-icon.png"
                      alt="Fast & Secure IRS Submission"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Fast & Secure IRS Submission
                </Typography>
                <Typography variant="body1">
                  Your completed return is sent electronically to the IRS
                  through secure, encrypted channels, ensuring speed, privacy,
                  and full compliance.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }} component="li">
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/real-time-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/real-time-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/real-time-icon.png"
                      alt="Real-Time Filing Status"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Real-Time Filing Status
                </Typography>
                <Typography variant="body1">
                  Get email alerts and in-account updates when your business
                  return is received, accepted, or needs attention—no guesswork,
                  no waiting.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }} component="li">
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/free-efile-retransmission-icon.png"
                      alt="Free Correction & Resubmission"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Free Correction & Resubmission
                </Typography>
                <Typography variant="body1">
                  If the IRS flags your return, you can make corrections and
                  resubmit at no additional charge—so you stay compliant without
                  paying twice.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
        component="section"
        aria-labelledby="switching-to-taxzerone"
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ mt: { xs: 2, sm: 6, md: 6 }, mb: { xs: 2, sm: 6, md: 10 } }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                id="switching-to-taxzerone"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                Why Businesses Are Switching to TaxZerone
              </Typography>
              {/* <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Why This Is Better Than Traditional Tax Software
              </Typography> */}
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }} textAlign="center">
              <Paper
                variant="outlined"
                sx={{ p: 3, borderRadius: 5, minHeight: '100%' }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    fontSize: { md: 20, sm: 18, xs: 16 },
                  }}
                >
                  Traditional Software{' '}
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    listStyleType: 'none',
                    paddingLeft: 0,
                    textAlign: 'left',
                    ml: { xs: 0, sm: 3 },
                    mt: { xs: 0, md: 3 },
                    mb: { xs: 0, md: 0 },
                    '& li': { display: 'flex' },
                  }}
                >
                  {' '}
                  <li
                    style={{
                      marginBottom: 20,
                      paddingBottom: 10,
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <DoneIcon sx={{ color: '#535252', mr: 1 }} />{' '}
                    <Box>You buy expensive software</Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 20,
                      paddingBottom: 10,
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <DoneIcon sx={{ color: '#535252', mr: 1 }} />{' '}
                    <Box>You choose forms manually</Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 20,
                      paddingBottom: 10,
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <DoneIcon sx={{ color: '#535252', mr: 1 }} />{' '}
                    <Box>Complex UI for accountants</Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 20,
                      paddingBottom: 10,
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <DoneIcon sx={{ color: '#535252', mr: 1 }} />{' '}
                    <Box>Separate extension tools</Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 0,
                    }}
                  >
                    <DoneIcon sx={{ color: '#535252', mr: 1 }} />{' '}
                    <Box>Hard to manage multiple entities </Box>
                  </li>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }} textAlign="center">
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 5,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                  borderWidth: 2,
                  borderColor: '#43247b',
                  position: 'relative',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    fontSize: { md: 20, sm: 18, xs: 16 },
                  }}
                >
                  TaxZerone
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    position: 'absolute',
                    backgroundColor: '#43247b',
                    color: '#fff',
                    top: -15,
                    // right: 16,
                    mx: 'auto',
                    px: 2.5,
                    py: 0.5,
                    borderRadius: 8,
                    fontWeight: 600,
                  }}
                >
                  Better
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    listStyleType: 'none',
                    paddingLeft: 0,
                    textAlign: 'left',
                    ml: { xs: 0, sm: 3 },
                    mt: { xs: 0, md: 3 },
                    mb: { xs: 0, md: 0 },
                    '& li': { display: 'flex' },
                  }}
                >
                  {' '}
                  <li
                    style={{
                      marginBottom: 20,
                      paddingBottom: 10,
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box> You only pay per filing</Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 20,
                      paddingBottom: 10,
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>System selects required forms</Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 20,
                      paddingBottom: 10,
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>Designed for both business owners and accountants</Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 20,
                      paddingBottom: 10,
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>Extensions included </Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 0,
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>Centralized business dashboard </Box>
                  </li>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        component="section"
      >
        <Container maxWidth="lg" sx={{ mb: 5, mt: 4 }}>
          <Grid
            container
            spacing={2}
            direction={{ xs: 'column-reverse', sm: 'row' }}
          >
            <Grid
              size={{ xs: 12, sm: 6, md: 6 }}
              sx={{
                mt: 5,
                mb: 3,
                justifyContent: 'center',
                alignContent: 'center',
              }}
            >
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mb: 1.5,
                    textAlign: 'left',
                  }}
                >
                  Trust & Compliance
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    textAlign: 'left',
                  }}
                >
                  Built for IRS-Level Accuracy
                </Typography>

                <Typography variant="body1" sx={{ textAlign: 'left' }}>
                  TaxZerone is an IRS-authorized e-file provider, meaning every
                  business return is transmitted directly to the IRS using their
                  secure electronic filing systems.
                </Typography>
                <Typography variant="body1" sx={{ textAlign: 'left', mt: 2 }}>
                  Your data is encrypted, validated, and verified before
                  submission — reducing rejection and penalty risks.
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }} sx={{ mt: 4 }}>
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  textAlign: { xs: 'center', md: 'right' },
                  my: { xs: 3, md: 0 },
                }}
                className="image-container"
              >
                <picture>
                  <source srcSet="/assets/images/business-tax-trust-img.webp" />
                  <source srcSet="/assets/images/business-tax-trust-img.png" />
                  <img
                    src="/assets/images/business-tax-trust-img.png"
                    alt="Trust & Compliance "
                    className="img-responsive img-shadow"
                    loading="lazy"
                    width={500}
                    height={500}
                  />
                </picture>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
        component="section"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            lineHeight: 1.5,
            mb: 3,
          }}
        >
          Start Your Business Filing the Right Way
        </Typography>
        <Typography variant="body1" color="white" sx={{ mb: 3, fontSize: 18 }}>
          Begin your e-filing journey and complete your business return with
          ease.{' '}
        </Typography>

        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=businesstax`}
          >
            Start Filing Now
          </Button>
        </Box>
      </Box>
      <Container
        maxWidth="lg"
        component="section"
        aria-labelledby="business-tax-faqs"
      >
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              id="business-tax-faqs"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                component="article"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  Is TaxZerone suitable for S-Corporations?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography variant="body1" itemProp="text" sx={{ mt: 1 }}>
                    Yes. TaxZerone is built to support full S-corporation
                    reporting — including Form 1120-S, shareholder allocations,
                    and Schedule K-1s — through our{' '}
                    <Link
                      href="/business-tax/efile-irs-form-1120-s/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      1120-S filing
                    </Link>{' '}
                    workflow in one connected platform rather than separate
                    tools.
                  </Typography>
                </Box>
              </Box>
              <Box
                component="article"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  Is TaxZerone IRS-authorized?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography variant="body1" itemProp="text" sx={{ mt: 1 }}>
                    Yes. TaxZerone is an IRS-authorized e-file provider, meaning
                    all returns are transmitted securely through official IRS
                    electronic systems with validation checks before submission.
                  </Typography>
                </Box>
              </Box>
              <Box
                component="article"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  Can I manage multiple businesses in one account?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography variant="body1" itemProp="text">
                    Yes. TaxZerone provides a centralized dashboard where you
                    can manage filings for multiple entities, track statuses,
                    and store records in one place.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default BusinessTax;
